import { setHeaderTitle } from '../state/actions/layout';
import store from '../state/configureStore';
import config from './config/config';
import { PAL_CARE_TITLE, SEA_DANCE_TITLE, SEMEDIX_TITLE, STATION_SATCOM_TITLE, VIKAND_TITLE } from './constants/appHeadConfig';
import { VIKAND, VIKAND_DIRECT, VIKAND_DIRECT_STAGE, SEAMEDEX_TYPE, ONSHIP_TITLE, FRONTM_TITLE } from './constants';
import { getAuthData } from '../services/storage/LocalStorage';
import axios from 'axios';
import { isFrontmAiDomain, isOnshipDomain, isPalCareDev2Domain, isSeaDanceDomain, isSeamedixDomain, isVikandDomain } from './DomainsHelper';
import { uuid } from 'short-uuid';
import { createUUID } from './Helpers';

const { v4: generateUUID } = require('uuid');

export const uuidGenerator = () => {
    return generateUUID();
};

export const parseBytesContent = (content) => {
    if (content && content.data && content.data.length > 0) {
        let utf8decoder = new TextDecoder();
        return JSON.parse(utf8decoder.decode(new Uint8Array(content.data)));
    }
    return content;
};


export const normalizeKeys = (data) => {
    const normalized = {};
    for (const key in data) {
        const normalizedKey = key.replace(/\s+/g, '_');
        normalized[normalizedKey] = data[key];
    }
    return normalized;
};

export const isCompleteUrl = (url) => {
    const pattern = /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/;
    return pattern.test(url);
};


export const getUserInitials = (name) => {
    if (!name) return '';

    const parts = name.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    const firstInitial = parts[0].charAt(0).toUpperCase();
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();

    return firstInitial + lastInitial;
};


export const isValidUrl = (url) => {
    if (!url || typeof url !== "string") return false;

    const base64Pattern = /^data:image\/[a-zA-Z]+;base64,[^\s]+$/;
    if (base64Pattern.test(url)) return true;

    if (url.startsWith("blob:")) return true;

    try {
        const parsedUrl = new URL(url);

        if (parsedUrl.pathname.includes("undefined")) {
            return false;
        }

        return true;
    } catch (_) {
        return false;
    }
};

export const handleApiError = (error) => {
    const status = error.response?.status;
    const details = error.response?.data || error.message;
    // Handle specific error codes
    if (status === 401 || error?.response?.data?.code === 16) {
        console.warn("Unauthorized. Redirecting to login...");
        localStorage.clear();
        window.location.href = "/";
    } else if (status === 500) {
        console.error("Server error. Please try again later.");
    } else {
        console.error("API Error:", details);
    }
};


export const isImageReachable = async (url) => {
    try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
    } catch (error) {
        console.warn("Image not reachable:", error);
        return false;
    }
};

export const getCurrentHostname = () => {
    try {
        return new URL(window.location.href).hostname;
    } catch (error) {
        console.error("Invalid domain URL:", error);
        return "";
    }
};

export const getDomainStatus = (targetDomain) => {
    const hostname = getCurrentHostname();
    return hostname === targetDomain;
};


export const updateAppHead = ({ title, favicon, description } = {}) => {
    if (title) {
        document.title = title;
    }

    if (favicon) {
        let link =
            document.querySelector("link[rel*='icon']") ||
            document.createElement("link");
        link.type = "image/png";
        link.rel = "shortcut icon";
        link.href = favicon;
        document.head.appendChild(link);
    }

    if (description) {
        let metaDescription = document.querySelector("meta[name='description']");
        if (!metaDescription) {
            metaDescription = document.createElement("meta");
            metaDescription.name = "description";
            document.head.appendChild(metaDescription);
        }
        metaDescription.content = description;
    }
};


export const getPageName = (isAnonymousUser) => {
    const path = window.location.pathname;

    // Paths allowed for anonymous users
    const publicPages = ["/app/", "/app/chats", "/app/loft", "/app/settings", "/app/messages", "/app/contacts"];

    // Define page names for known paths
    const pageMap = {
        "/app/": "Home",
        "/app/chats": "Home",
        "/app/contacts": "Contacts",
        "/app/groups": "Channels",
        "/app/catalog": "Catalog",
        "/app/my-profile": "Profile",
        "/app/home": "Welcome",
        "/app/loft": "Loft",
        "/app/settings": "Settings",
        "/app/messages": 'Messages'
    };

    if (path.includes("/app/chats") || path === "/app/") return "Home";
    if (path.includes("/app/messages") || path === "/app/") return "Messages";
    if (path.includes("/app/contacts") || path === "/app/") return "Contacts";
    if (path.startsWith("/app/catalog/categories") && !isAnonymousUser) return "Catalog_Categories";
    if (path.startsWith("/app/catalog/featured") && !isAnonymousUser) return "Catalog_Featured";
    if (path.startsWith("/app/catalog") && !isAnonymousUser) return "Catalog";

    // Check direct matches
    if (pageMap[path]) {
        // Restrict if anonymous user
        if (isAnonymousUser && !publicPages.includes(path)) return "Error";
        return pageMap[path];
    }

    // Fallback
    return "Error";
};


export const setPageTitle = () => {
    if (window.location.pathname.includes('contacts')) {
        window.document.title = "Contacts";
        store.dispatch(setHeaderTitle('Contacts'));
    }
    else if (window.location.pathname.includes('profile')) {
        store.dispatch(setHeaderTitle('My Profile'));
        window.document.title = "My Profile";
    }
    else if (window.location.pathname.includes('settings')) {
        store.dispatch(setHeaderTitle('Settings'));
        window.document.title = "Settings";
    }
    else if (window.location.pathname.includes('catalog')) {
        store.dispatch(setHeaderTitle('Apps'));
    }
    else if (window.location.pathname.includes('messages')) {
        store.dispatch(setHeaderTitle('Connect'));
    }
    else if (window.location.pathname === '/change-password') {
        store.dispatch(setHeaderTitle('Change Password'));
        window.document.title = "Change Password";
    }
    else if (window.location.pathname === '/') {
        window.document.title = "Station Satcom";
    }
};

export const isValidImageUrl = async (imageUrl) => {
    if (!imageUrl) {
        console.log("Image URL is undefined or empty");
        return false;
    }

    try {
        const response = await fetch(imageUrl, { method: "GET" });
        if (response.ok) {
            return imageUrl;
        } else {
            return false;
        }
    } catch (error) {
        console.log("");
        return false;
    }
};

export const getUserProfileImageUrl = (userId) => {
    return `${config.userImageBaseUrl}/${userId}.png`;
};

export const getUserProfileImageUrlThumbnail = (userId) => {
    return `${config.userImageBaseUrl}/${userId}_75x75.png`;
};
export const getGroupProfileImageUrl = (groupId) => {
 return  `${config.contentURL}channelLogos/${groupId}_75x75.png`;
    
}
export const cleanHtmlContent = (htmlString) => {
    // Remove newline characters and replace with spaces
    let cleanedHtml = htmlString.replace(/\\n/g, ' ');

    // Remove or replace other unwanted characters if necessary
    cleanedHtml = cleanedHtml.replace(/\\r/g, '');
    cleanedHtml = cleanedHtml.replace(/\\t/g, '');

    // Handle other special characters if needed
    // For example, replace special characters like `` if present
    cleanedHtml = cleanedHtml.replace(//g, ''); // Replace with empty string or appropriate value
    cleanedHtml = cleanedHtml.replace(/^\[\"/, '').replace(/\"\]$/, '');

    return cleanedHtml;
};

export const getDomainName = () => {
    if (isPalCareDev2Domain) {
        return PAL_CARE_TITLE;
    }
    else if (isSeamedixDomain) {
        return SEMEDIX_TITLE;
    }
    else if (isVikandDomain) {
        return VIKAND_TITLE;
    }
    else if (isSeaDanceDomain) {
        return SEA_DANCE_TITLE;
    }
    else if (isOnshipDomain && isFrontmAiDomain) {
        return FRONTM_TITLE;
    }
    else if (isOnshipDomain) {
        return ONSHIP_TITLE;
    }

    else {
        return STATION_SATCOM_TITLE;
    }
};


export const deRegisterPushNotification = (selectedDomainDetails) => {
    if (selectedDomainDetails.userDomain) {
        let currentUserDomain = selectedDomainDetails.userDomain
        let sessionId = getAuthData()?.auth?.sessionId;
        let userEmail = getAuthData()?.user?.emailAddress;
        let baseURL = `${config.gRPCURL}/grpc/user.UserService/DeregisterWebApp`;
        let webPushAppType;
        let shouldSendPushNotification = false;
        let pushKey;
        if (currentUserDomain === VIKAND || currentUserDomain === VIKAND_DIRECT || currentUserDomain === VIKAND_DIRECT_STAGE) {
            shouldSendPushNotification = true;
            webPushAppType = config.vikandWebAppType;
            pushKey = config.vikandWebPushKey;
        }
        if (currentUserDomain === SEAMEDEX_TYPE) {
            shouldSendPushNotification = true;
            webPushAppType = config.seamedexAppType;
            pushKey = config.seamedexWebPushKey;
        }
        if (shouldSendPushNotification) {
            const config = {
                pushKey,
            };
            async function subscribe(topic) {
                navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
                    serviceWorkerRegistration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlB64ToUint8Array(config.pushKey),
                    }).then((subscription) => {
                        console.log("<=============== De-Register ============>", subscription)
                        const subscriptionData = JSON.stringify(subscription);
                        const subscriptionInfo = JSON.parse(subscriptionData);
                        if (!subscription) {
                            navigator.serviceWorker.ready.then((reg) => {
                                reg.pushManager.getSubscription().then((subscription) => {
                                    subscription
                                        .unsubscribe()
                                        .then((successful) => { })
                                        .catch((e) => { });
                                });
                            });
                        }

                        if (subscription) {
                            // Posting a request to grpc using axios once we get the subscription
                            const info = {
                                endpoint: subscriptionInfo?.endpoint,
                            };
                            let data = JSON.stringify({
                                subscription: JSON.stringify(info),
                                webPushAppType: webPushAppType,
                            });

                            let config = {
                                method: "post",
                                url: baseURL,
                                headers: {
                                    "Content-Type": "application/json",
                                    sessionId: sessionId,
                                },
                                data: data,
                            };

                            axios(config)
                                .then(async (response) => {
                                    console.log("Notificaiton API DEREGISTER Success", response)
                                })
                                .catch(function (error) {
                                    console.log("Notification API DEREGISTER error", error)
                                    console.log("Notification API DEREGISTER error response", error?.response)
                                });
                        }
                    });
                });
            }
            function urlB64ToUint8Array(base64String) {
                const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
                const base64 = (base64String + padding)
                    .replace(/\-/g, "+")
                    .replace(/_/g, "/");

                const rawData = window.atob(base64);
                const outputArray = new Uint8Array(rawData.length);

                for (let i = 0; i < rawData.length; ++i) {
                    outputArray[i] = rawData.charCodeAt(i);
                }
                return outputArray;
            }
            subscribe(userEmail);
        }
    }
};

